import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import WeaponArt from './WeaponArt.jsx'
import CoinIcon from './CoinIcon.jsx'
import { formatCoinsNum, RARITY_COLORS } from '../lib/format.js'
import { preloadSounds, playTickSound, playWinSound } from '../lib/sound.js'
import '../styles/roulette.css'

const STRIP_LENGTH = 65
const IDLE_INDEX = 5    // Стартовая позиция покоя до спина
const WINNER_INDEX = 50 // Точная позиция остановки выигрыша
// Кривая с мощным быстрым разгоном в начале и плавным кинематографичным замедлением в конце
const EASING = 'cubic-bezier(0.12, 0.85, 0.45, 1)'

// Размеры карточек (пиксель в пиксель под центральную рамку)
const HORIZONTAL_TILE_WIDTH = 164
const HORIZONTAL_GAP = 8
const HORIZONTAL_STEP = HORIZONTAL_TILE_WIDTH + HORIZONTAL_GAP // 172px

const VERTICAL_TILE_HEIGHT = 200
const VERTICAL_GAP = 10
const VERTICAL_STEP = VERTICAL_TILE_HEIGHT + VERTICAL_GAP // 210px

const MULTI_VIEWPORT_HEIGHT = 340

function getTransformOffset(el, isHorizontal) {
  if (!el) return 0
  const style = window.getComputedStyle(el)
  const transform = style.transform || style.webkitTransform
  if (!transform || transform === 'none') return 0
  if (window.DOMMatrix) {
    try {
      const m = new DOMMatrix(transform)
      return Math.abs(isHorizontal ? m.m41 : m.m42)
    } catch {}
  }
  const values = transform.match(/matrix.*\((.+)\)/)
  if (values && values[1]) {
    const parts = values[1].split(',').map((v) => parseFloat(v.trim()))
    if (parts.length === 6) {
      return Math.abs(isHorizontal ? parts[4] : parts[5])
    }
    if (parts.length === 16) {
      return Math.abs(isHorizontal ? parts[12] : parts[13])
    }
  }
  return 0
}

function createRandomStrip(pool, winner = null, startItem = null) {
  if (!pool || pool.length === 0) return []
  const tiles = []
  for (let i = 0; i < STRIP_LENGTH; i++) {
    tiles.push(pool[Math.floor(Math.random() * pool.length)])
  }
  if (startItem) {
    tiles[IDLE_INDEX] = startItem
  }
  if (winner) {
    tiles[WINNER_INDEX] = winner
  }
  return tiles
}

export default function Roulette({
  pool = [],
  winners = [],
  count = 1,
  spinning = false,
  fastMode = false,
  soundEnabled = true,
  onFinish,
  onSellSingle,
  soldIds = [],
  sellingItemIds = [],
}) {
  const [landed, setLanded] = useState(false)
  const [offsets, setOffsets] = useState([])
  const [transitionStyle, setTransitionStyle] = useState([])
  const viewportRef = useRef(null)
  const firstTrackRef = useRef(null)
  const finishCallbackRef = useRef(onFinish)
  const soundRef = useRef(soundEnabled)

  // Предзагрузка звуков Musor Drop при старте
  useEffect(() => {
    preloadSounds().catch(() => {})
  }, [])

  useEffect(() => {
    finishCallbackRef.current = onFinish
  }, [onFinish])

  useEffect(() => {
    soundRef.current = soundEnabled
  }, [soundEnabled])

  const poolKey = useMemo(() => {
    if (!pool || pool.length === 0) return ''
    return pool.map((item) => item.id || item.itemId || item.name).join(',')
  }, [pool])

  // 5 постоянных случайных лент (по одной на каждый возможный барабан от 1 до 5)
  const [reels, setReels] = useState(() => {
    if (!pool || pool.length === 0) return []
    return Array.from({ length: 5 }, () => createRandomStrip(pool))
  })

  // При первой загрузке кейса или переходе на другой кейс генерируем живые случайные барабаны
  useEffect(() => {
    if (!pool || pool.length === 0) return
    setReels(Array.from({ length: 5 }, () => createRandomStrip(pool)))
    setLanded(false)
  }, [poolKey])

  // Сброс состояния выпадения при очистке дропов
  useEffect(() => {
    if (!winners || winners.length === 0) {
      setLanded(false)
    }
  }, [winners])

  // Активные барабаны для текущего режима (count: 1..5)
  const activeReels = useMemo(() => {
    const reelCount = Math.max(1, Math.min(5, count))
    return reels.slice(0, reelCount)
  }, [reels, count])

  // Точный расчёт смещения для центровки карточки ровно по середине полупрозрачной рамки (без смещений/джиттера)
  const calculateCenterOffset = (targetIndex, forcedCount = count) => {
    const viewportW = viewportRef.current?.clientWidth || 1000

    if (forcedCount === 1) {
      const centerH = viewportW / 2 - HORIZONTAL_TILE_WIDTH / 2
      const target = targetIndex * HORIZONTAL_STEP - centerH
      return [target]
    } else {
      const colEl = firstTrackRef.current?.parentElement
      const colH = colEl?.clientHeight || 336
      const centerV = Math.round((colH - VERTICAL_TILE_HEIGHT) / 2) // ровно (336 - 200) / 2 = 68px
      const target = targetIndex * VERTICAL_STEP - centerV
      const reelCount = Math.max(1, Math.min(5, forcedCount))
      return Array.from({ length: reelCount }, () => target)
    }
  }

  // При смене режима (count) или когда не крутится — гарантированно держим карточку по центру
  useLayoutEffect(() => {
    if (spinning) return

    const applyRestPosition = () => {
      if (!viewportRef.current || activeReels.length === 0) return
      const targetIndex = landed ? WINNER_INDEX : IDLE_INDEX
      const centered = calculateCenterOffset(targetIndex, count)
      setOffsets(centered)
      setTransitionStyle(activeReels.map(() => 'none'))
    }

    applyRestPosition()

    const ro = new ResizeObserver(() => {
      if (!spinning) {
        applyRestPosition()
      }
    })
    if (viewportRef.current) ro.observe(viewportRef.current)

    return () => ro.disconnect()
  }, [activeReels.length, count, spinning, landed])

  // Валидные оффсеты для текущего рендера (защита от скачков при переключении х1..х5)
  const currentTargetIndex = landed ? WINNER_INDEX : IDLE_INDEX
  const validOffsets = useMemo(() => {
    if (offsets.length === activeReels.length) return offsets
    return calculateCenterOffset(currentTargetIndex, count)
  }, [offsets, activeReels.length, count, currentTargetIndex])

  // Запуск крутки (с быстрым разгоном в начале, большей длительностью и точной остановкой по центру)
  useEffect(() => {
    if (!spinning || !viewportRef.current || activeReels.length === 0 || !pool || pool.length === 0) return

    const reelCount = Math.max(1, Math.min(5, count))

    // 1. Создаём динамичные случайные ленты под текущую крутку с реальными дропами
    const spinReels = Array.from({ length: 5 }, (_, colIdx) => {
      const winner = winners?.[colIdx] ?? winners?.[0] ?? null
      const currentStrip = reels[colIdx] || []
      const startItem = currentStrip[landed ? WINNER_INDEX : IDLE_INDEX] || pool[Math.floor(Math.random() * pool.length)]
      return createRandomStrip(pool, winner, startItem)
    })
    setReels(spinReels)
    setLanded(false)

    // Длительность спина (6.2 сек обычная, 0.65 сек быстрая с динамичным разгоном)
    const baseDuration = fastMode ? 650 : 6200
    const colStagger = fastMode ? 70 : 250
    const viewport = viewportRef.current

    // 1. Сброс барабанов в стартовую позицию разгона (IDLE_INDEX) БЕЗ анимации
    const startOffsets = calculateCenterOffset(IDLE_INDEX, count)
    setTransitionStyle(Array.from({ length: reelCount }, () => 'none'))
    setOffsets(startOffsets)

    // Принудительный reflow, чтобы браузер зафиксировал начальную точку разгона
    if (viewport) {
      void viewport.offsetWidth
    }

    // 2. Целевая точка остановки — ровно WINNER_INDEX (погрешность 0px — идеальный центр!)
    const targetOffsets = calculateCenterOffset(WINNER_INDEX, count)

    // 3. Запуск анимации со следующего кадра
    let rafId = null
    const spinEasing = fastMode ? 'cubic-bezier(0.1, 0.85, 0.25, 1)' : EASING
    rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setTransitionStyle(
          Array.from({ length: reelCount }, (_, colIdx) => {
            const colDuration = baseDuration + colIdx * colStagger
            return `transform ${colDuration}ms ${spinEasing}`
          }),
        )
        setOffsets(targetOffsets)
      })
    })

    // 4. Трещотка звуков прокрутки: 100% синхронизирована с реальным движением ленты (rAF + getComputedStyle)
    let soundRafId = null
    if (soundRef.current) {
      let lastCardIndex = IDLE_INDEX
      let lastTickTime = 0
      const isHorizontal = count === 1
      const step = isHorizontal ? HORIZONTAL_STEP : VERTICAL_STEP
      const viewportW = viewport.clientWidth || 1000
      const centerPad = isHorizontal
        ? viewportW / 2 - HORIZONTAL_TILE_WIDTH / 2
        : Math.round(((firstTrackRef.current?.parentElement?.clientHeight || 336) - VERTICAL_TILE_HEIGHT) / 2)

      const minInterval = fastMode ? 18 : 32 // Защита от наложения щелчков на гипер-скорости

      const trackReelPosition = () => {
        const trackEl = firstTrackRef.current
        if (trackEl && soundRef.current) {
          const currentOffset = getTransformOffset(trackEl, isHorizontal)
          if (currentOffset > 0) {
            // Текущий индекс карточки под центральным указателем
            const currentCard = Math.round((currentOffset + centerPad) / step)

            if (
              currentCard !== lastCardIndex &&
              currentCard > IDLE_INDEX &&
              currentCard <= WINNER_INDEX
            ) {
              const now = performance.now()
              if (now - lastTickTime >= minInterval) {
                playTickSound(0.20)
                lastTickTime = now
              }
              lastCardIndex = currentCard
            }
          }
        }
        soundRafId = requestAnimationFrame(trackReelPosition)
      }

      soundRafId = requestAnimationFrame(trackReelPosition)
    }

    // 5. Окончание прокрутки всех колонок с паузой перед показом модалки выигрыша
    const totalDuration = baseDuration + (reelCount - 1) * colStagger
    let modalTimer = null
    const finishTimer = setTimeout(() => {
      setLanded(true)
      if (soundRef.current) {
        playWinSound(0.38)
      }
      const modalDelay = fastMode ? 140 : 450
      modalTimer = setTimeout(() => {
        finishCallbackRef.current?.()
      }, modalDelay)
    }, totalDuration)

    return () => {
      if (rafId) cancelAnimationFrame(rafId)
      if (soundRafId) cancelAnimationFrame(soundRafId)
      clearTimeout(finishTimer)
      if (modalTimer) clearTimeout(modalTimer)
    }
  }, [spinning, count, fastMode])

  if (activeReels.length === 0) {
    return (
      <div className="cm-roulette-viewport is-single" style={{ display: 'grid', placeItems: 'center' }}>
        <span style={{ color: '#756A86', fontWeight: 600 }}>Загрузка рулетки…</span>
      </div>
    )
  }

  const singleOffset = validOffsets[0] ?? calculateCenterOffset(IDLE_INDEX, 1)[0] ?? 0
  const singleTransition = transitionStyle[0] || 'none'

  return (
    <div
      ref={viewportRef}
      className={`cm-roulette-viewport ${count === 1 ? 'is-single' : 'is-multi'} is-count-${count}`}
    >
      {count === 1 ? (
        <div key="single" className="cm-roulette-track-single">
          {/* Затемнения по краям */}
          <div className="cm-roulette-fade-left" />
          <div className="cm-roulette-fade-right" />

          {/* Кибер-прицел в центре (без проходящей через весь экран линии-хвоста) */}
          <div
            className={`cm-roulette-reticle ${landed ? 'is-landed' : ''}`}
            style={landed && reels[0]?.[WINNER_INDEX] ? { '--winner-rarity': RARITY_COLORS[reels[0][WINNER_INDEX].rarity] || '#a855f7' } : undefined}
          >
            <span className="cm-roulette-pointer-top" />
            <span className="cm-roulette-pointer-bottom" />
          </div>

          {/* Горизонтальная лента */}
          <div
            ref={firstTrackRef}
            className="cm-roulette-strip-horizontal"
            style={{
              transform: `translate3d(${-singleOffset}px, 0, 0)`,
              transition: singleTransition,
            }}
          >
            {reels[0]?.map((item, idx) => {
              if (!item) return null
              const isWinner = landed && idx === WINNER_INDEX
              const isCenterIdle = !spinning && !landed && idx === IDLE_INDEX

              return (
                <div
                  key={`${item.id || item.itemId}-${idx}`}
                  className={`cm-roulette-tile ${isWinner ? 'is-winner' : ''} ${isCenterIdle ? 'is-center-idle' : ''}`}
                  style={{ '--rarity': RARITY_COLORS[item.rarity] ?? '#a855f7' }}
                >
                  <WeaponArt image={item.image} rarity={item.rarity} title={item.skin} size="lg" />
                  <span className="cm-roulette-tile-name">{item.name}</span>
                  <span className="cm-roulette-tile-skin">{item.skin}</span>
                </div>
              )
            })}
          </div>
        </div>
      ) : (
        <div key="multi" className="cm-roulette-track-multi">
          {/* Затемнения сверху и снизу */}
          <div className="cm-roulette-fade-top" />
          <div className="cm-roulette-fade-bottom" />

          {/* Колонки барабанов */}
          <div className="cm-roulette-columns">
            {activeReels.map((strip, colIdx) => {
              const offset = validOffsets[colIdx] ?? calculateCenterOffset(IDLE_INDEX, count)[colIdx] ?? 0
              const transition = transitionStyle[colIdx] || 'none'
              const winner = strip[WINNER_INDEX]
              const isWinnerSold = landed && winner && soldIds.includes(winner.inventoryId)
              const isWinnerSelling = landed && winner && sellingItemIds.includes(winner.inventoryId)

              return (
                <div key={colIdx} className="cm-roulette-column">
                  {/* Центральная рамка на каждой колонке со стрелками-указателями */}
                  <div
                    className={`cm-roulette-column-frame ${landed ? 'is-landed' : ''}`}
                    style={landed && strip[WINNER_INDEX] ? { '--winner-rarity': RARITY_COLORS[strip[WINNER_INDEX].rarity] || '#a855f7' } : undefined}
                  >
                    <span className="cm-roulette-pointer-top" />
                    <span className="cm-roulette-pointer-bottom" />
                  </div>

                  {/* Вертикальная лента предметов */}
                  <div
                    ref={colIdx === 0 ? firstTrackRef : undefined}
                    className="cm-roulette-strip-vertical"
                    style={{
                      transform: `translate3d(0, ${-offset}px, 0)`,
                      transition,
                    }}
                  >
                    {strip.map((item, idx) => {
                      if (!item) return null
                      const isWinner = landed && idx === WINNER_INDEX
                      const isCenterIdle = !spinning && !landed && idx === IDLE_INDEX

                      return (
                        <div
                          key={`${item.id || item.itemId}-${colIdx}-${idx}`}
                          className={`cm-roulette-tile-vertical ${isWinner ? 'is-winner' : ''} ${isCenterIdle ? 'is-center-idle' : ''}`}
                          style={{ '--rarity': RARITY_COLORS[item.rarity] ?? '#a855f7' }}
                        >
                          <WeaponArt image={item.image} rarity={item.rarity} title={item.skin} size={count <= 3 ? 'xl' : 'lg'} />
                          <span className="cm-roulette-tile-vertical-name">{item.name}</span>
                          <span className="cm-roulette-tile-vertical-skin">{item.skin}</span>
                        </div>
                      )
                    })}
                  </div>

                  {/* Кнопка ПРОДАТЬ ниже карточки в барабане */}
                  {landed && winner && (
                    <div className="cm-column-action-bar">
                      <button
                        type="button"
                        className={`cm-tile-sell-btn ${isWinnerSold ? 'is-sold' : ''}`}
                        onClick={(e) => {
                          e.stopPropagation()
                          onSellSingle?.(winner)
                        }}
                        disabled={isWinnerSold || isWinnerSelling}
                        title={isWinnerSold ? 'Предмет продан' : `Продать за ${formatCoinsNum(winner.priceKopecks)}`}
                      >
                        {isWinnerSold ? (
                          <span className="cm-tile-sell-label">✓ Продано</span>
                        ) : isWinnerSelling ? (
                          <span className="cm-tile-sell-label">Продаём…</span>
                        ) : (
                          <>
                            <span className="cm-tile-sell-label">Продать</span>
                            <span className="cm-tile-sell-price">
                              <span>{formatCoinsNum(winner.priceKopecks)}</span>
                              <CoinIcon size="xs" />
                            </span>
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
